// F-107 : demandes des personnes transmises à l'espace, gels pour litige, anonymisation d'une candidature sur demande.
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { post } from "@core/api";
import { date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Modal, Notice, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { useAction } from "./shared";

const KIND_LABELS: Record<string, string> = {
  acces: "Accès aux données",
  export: "Export des données",
  rectification: "Correction des données",
  effacement: "Effacement des données",
  opposition: "Opposition à un traitement",
  limitation: "Limitation d’un traitement",
};

const ORG_STATUS: Record<string, { label: string; tone: "attention" | "succes" | "erreur" }> = {
  a_traiter: { label: "À traiter", tone: "attention" },
  traitee: { label: "Traitée", tone: "succes" },
  refusee: { label: "Refusée", tone: "erreur" },
};

/** Motifs d'anonymisation hors durée : liste fermée, identique à celle du serveur. */
export const MOTIVES = [
  { value: "demande_effacement", label: "Demande d’effacement de la personne" },
  { value: "opposition", label: "Opposition de la personne au traitement" },
  { value: "selection_terminee", label: "Sélection terminée : conservation plus nécessaire" },
];

type Request = {
  id: string;
  kind: string;
  message: string;
  due_on: string;
  created_at: string;
  requester_name: string | null;
  requester_email: string | null;
  application_ids: string[];
  status: string;
  response: string;
  handled_at: string | null;
};

/** Onglet « Demandes des personnes » de la page Données personnelles. */
export function RightsRequestsTab() {
  const org = useOrg();
  const q = useGet<{ items: Request[] }>(org.api("/rights-requests"));
  const [answering, setAnswering] = useState<Request | null>(null);
  const [anonymizing, setAnonymizing] = useState<Request | null>(null);
  const owner = org.role === "proprietaire";
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const items = q.data?.items ?? [];
  return (
    <div className="stack">
      <Notice tone="info">
        Les personnes exercent leurs droits depuis leur compte (« Mes données »). Les demandes qui concernent votre espace arrivent ici avec les candidatures
        concernées. Répondez en indiquant ce qui a été fait, ou pourquoi la demande est refusée : la réponse est ajoutée au suivi de la personne.
      </Notice>
      {items.length === 0 ? (
        <Empty title="Aucune demande reçue.">Les demandes d’accès, de correction, d’opposition ou d’effacement transmises à votre espace apparaîtront ici.</Empty>
      ) : (
        <Card title="Demandes des personnes">
          <Table caption="Demandes d’exercice des droits">
            <thead>
              <tr>
                <th>Reçue le</th>
                <th>Personne</th>
                <th>Demande</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const st = ORG_STATUS[r.status] ?? { label: r.status, tone: "attention" as const };
                return (
                  <tr key={r.id}>
                    <td>
                      {dateTime(r.created_at)}
                      {r.status === "a_traiter" ? <span className="block small muted">Répondre avant le {date(r.due_on)}</span> : null}
                    </td>
                    <td>
                      {r.requester_name ?? "Compte effacé"}
                      {r.requester_email && !r.requester_email.endsWith("@efface.invalid") ? <span className="block small muted">{r.requester_email}</span> : null}
                    </td>
                    <td>
                      {KIND_LABELS[r.kind] ?? r.kind}
                      {r.message ? <span className="block small">{r.message}</span> : null}
                      {r.application_ids.map((id, i) => (
                        <Link key={id} className="block small" to={org.to(`candidatures/${id}`)}>
                          Candidature concernée{r.application_ids.length > 1 ? ` ${i + 1}` : ""}
                        </Link>
                      ))}
                      {r.response ? <span className="block small muted">Réponse : {r.response}</span> : null}
                    </td>
                    <td>
                      <Badge tone={st.tone}>{st.label}</Badge>
                      {r.handled_at ? <span className="block small muted">le {dateTime(r.handled_at)}</span> : null}
                    </td>
                    <td className="right">
                      {r.status === "a_traiter" ? (
                        <div className="stack-sm">
                          <Button small variant="secondaire" onClick={() => setAnswering(r)}>
                            Répondre
                          </Button>
                          {owner && r.application_ids.length && (r.kind === "effacement" || r.kind === "opposition") ? (
                            <Button small variant="danger" onClick={() => setAnonymizing(r)}>
                              Anonymiser la candidature
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}
      <AnswerModal request={answering} onClose={() => setAnswering(null)} />
      {anonymizing ? (
        <AnonymizeModal
          open
          applicationIds={anonymizing.application_ids}
          requestId={anonymizing.id}
          defaultMotive={anonymizing.kind === "opposition" ? "opposition" : "demande_effacement"}
          onClose={() => setAnonymizing(null)}
        />
      ) : null}
    </div>
  );
}

function AnswerModal({ request, onClose }: { request: Request | null; onClose: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const action = useAction();
  const [status, setStatus] = useState("traitee");
  const [response, setResponse] = useState("");
  const close = () => {
    action.setError(null);
    setResponse("");
    setStatus("traitee");
    onClose();
  };
  const submit = async () => {
    if (!request) return;
    const r = await action.run(() => post<any>(org.api(`/rights-requests/${request.id}/response`), { status, response }));
    if (r) {
      toast(r.request_closed ? "Réponse envoyée : la demande est close." : "Réponse enregistrée ; d’autres espaces doivent encore répondre.");
      invalidate(org.api("/rights-requests"));
      close();
    }
  };
  return (
    <Modal
      open={!!request}
      onClose={close}
      title={request ? `Répondre : ${KIND_LABELS[request.kind] ?? request.kind}` : ""}
      footer={
        <>
          <Button variant="discret" onClick={close}>
            Annuler
          </Button>
          <Button busy={action.busy} onClick={submit}>
            Envoyer la réponse
          </Button>
        </>
      }
    >
      <div className="stack-sm">
        {request?.message ? <Notice tone="info">{request.message}</Notice> : null}
        <Select
          label="Issue"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          error={fieldError(action.error, "status")}
          options={[
            { value: "traitee", label: "Demande traitée" },
            { value: "refusee", label: "Demande refusée (motif à indiquer)" },
          ]}
        />
        <Textarea
          label="Réponse à la personne"
          required
          value={response}
          help="Ce qui a été fait, ou pourquoi la demande est refusée (par exemple : données conservées au titre du bail en cours). La personne voit ce texte."
          error={fieldError(action.error, "response")}
          onChange={(e) => setResponse(e.target.value)}
        />
        <ErrorBox error={action.error && !fieldError(action.error, "response") && !fieldError(action.error, "status") ? action.error : null} />
      </div>
    </Modal>
  );
}

/** Anonymisation d'une candidature précise : propriétaire de l'espace, motif en liste fermée, confirmation explicite. */
export function AnonymizeModal({
  open,
  applicationIds,
  requestId,
  defaultMotive = "demande_effacement",
  onClose,
  onDone,
}: {
  open: boolean;
  applicationIds: string[];
  requestId?: string;
  defaultMotive?: string;
  onClose: () => void;
  onDone?: () => void;
}) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const action = useAction();
  const [application, setApplication] = useState(applicationIds[0] ?? "");
  const [motive, setMotive] = useState(defaultMotive);
  const [confirmed, setConfirmed] = useState(false);
  const close = () => {
    action.setError(null);
    setConfirmed(false);
    onClose();
  };
  const submit = async () => {
    const body: Record<string, unknown> = { motive };
    if (requestId) body.request_id = requestId;
    const r = await action.run(() => post<any>(org.api(`/retention/applications/${application}/anonymize`), body));
    if (r) {
      toast(r.result?.verified ? "Candidature anonymisée ; effacement vérifié et inscrit au journal." : "Candidature anonymisée ; vérification incomplète, voir le journal des effacements.");
      invalidate(org.api("/rights-requests"), org.api("/retention"));
      close();
      onDone?.();
    }
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title="Anonymiser une candidature"
      footer={
        <>
          <Button variant="discret" onClick={close}>
            Annuler
          </Button>
          <Button variant="danger" busy={action.busy} disabled={!confirmed || !application} onClick={submit}>
            Anonymiser définitivement
          </Button>
        </>
      }
    >
      <div className="stack-sm">
        <Notice tone="attention" title="Action irréversible">
          Les pièces sont effacées, l’identité, les coordonnées, les valeurs lues, les échanges et les réponses sont retirés dans tous les modules. Restent des traces sans
          donnée personnelle (statuts, dates, décision et motifs codés). Une candidature encore active est retirée. Une candidature retenue, liée à un bail ou gelée pour
          litige n’est pas anonymisée : répondez alors à la demande en justifiant la conservation.
        </Notice>
        {applicationIds.length > 1 ? (
          <Select
            label="Candidature"
            value={application}
            onChange={(e) => setApplication(e.target.value)}
            options={applicationIds.map((id, i) => ({ value: id, label: `Candidature ${i + 1} (${id.slice(0, 8)})` }))}
          />
        ) : null}
        <Select label="Motif" value={motive} onChange={(e) => setMotive(e.target.value)} error={fieldError(action.error, "motive")} options={MOTIVES} />
        <Checkbox label="Je confirme l’anonymisation définitive de cette candidature." checked={confirmed} onChange={setConfirmed} />
        <ErrorBox error={action.error && !fieldError(action.error, "motive") ? action.error : null} />
      </div>
    </Modal>
  );
}

type Hold = { id: string; subject_id: string; reason: string; until_on: string; created_by_name: string | null; created_at: string; released_at: string | null; active: boolean };

/** Gels pour litige de l'espace (page Données personnelles, onglet Conservation). */
export function HoldsCard() {
  const org = useOrg();
  const q = useGet<{ items: Hold[] }>(org.api("/retention/holds"));
  const owner = org.role === "proprietaire";
  const items = q.data?.items ?? [];
  return (
    <Card title="Gels pour litige">
      <p className="small">
        Une candidature concernée par une contestation, une réclamation ou une procédure peut être gelée : la tâche de conservation ne l’anonymise pas avant
        l’échéance du gel, au plus la durée de la règle « litige ». Un gel se pose depuis l’onglet « Conservation » de la candidature.
      </p>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !items.length ? <p className="small muted">Aucun gel.</p> : null}
      {items.length ? <HoldsTable holds={items} owner={owner} withLink /> : null}
    </Card>
  );
}

function HoldsTable({ holds, owner, withLink }: { holds: Hold[]; owner: boolean; withLink?: boolean }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const action = useAction();
  const release = async (id: string) => {
    const r = await action.run(() => post(org.api(`/retention/holds/${id}/release`), {}));
    if (r) invalidate(org.api("/retention"));
  };
  return (
    <div className="stack-sm">
      <ErrorBox error={action.error} />
      <Table caption="Gels pour litige">
        <thead>
          <tr>
            <th>Motif</th>
            <th>Échéance</th>
            <th>État</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {holds.map((h) => (
            <tr key={h.id}>
              <td>
                {h.reason}
                <span className="block small muted">
                  Posé le {dateTime(h.created_at)}
                  {h.created_by_name ? ` par ${h.created_by_name}` : ""}
                </span>
                {withLink ? (
                  <Link className="block small" to={org.to(`candidatures/${h.subject_id}`)}>
                    Voir la candidature
                  </Link>
                ) : null}
              </td>
              <td>{date(h.until_on)}</td>
              <td>{h.active ? <Badge tone="attention">En cours</Badge> : <Badge>{h.released_at ? `Levé le ${date(h.released_at)}` : "Échu"}</Badge>}</td>
              <td className="right">
                {owner && h.active ? (
                  <Button small variant="secondaire" busy={action.busy} onClick={() => release(h.id)}>
                    Lever le gel
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

/** Onglet « Conservation » de la candidature (côté bailleur) : gel pour litige et anonymisation sur demande. */
export function ApplicationRetentionTab({ applicationId }: { orgId: string; applicationId: string }) {
  const org = useOrg();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const allowed = org.can("members");
  const owner = org.role === "proprietaire";
  const holds = useGet<{ items: Hold[] }>(allowed ? org.api("/retention/holds") : null);
  const action = useAction();
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [anonymizing, setAnonymizing] = useState(false);
  if (!allowed) return <Notice tone="info">La conservation des candidatures est gérée par les propriétaires et gestionnaires de l’espace.</Notice>;
  const mine = (holds.data?.items ?? []).filter((h) => h.subject_id === applicationId);
  const active = mine.some((h) => h.active);
  const createHold = async () => {
    const r = await action.run(() => post(org.api("/retention/holds"), { application_id: applicationId, reason, until_on: until }));
    if (r) {
      setReason("");
      setUntil("");
      invalidate(org.api("/retention"));
    }
  };
  return (
    <div className="stack">
      <Notice tone="info">
        Cette candidature suit les règles de conservation de l’espace (page « Données personnelles ») : une candidature non retenue ou retirée est anonymisée
        automatiquement à l’échéance, sauf gel pour litige en cours.
      </Notice>
      <Card title="Gel pour litige">
        {holds.isLoading ? <Loading /> : null}
        <ErrorBox error={holds.error} />
        {mine.length ? <HoldsTable holds={mine} owner={owner} /> : <p className="small muted">Aucun gel sur cette candidature.</p>}
        {owner && !active ? (
          <div className="stack-sm">
            <Textarea label="Motif du gel" required value={reason} help="Contestation, réclamation ou procédure : décrivez le litige sans données inutiles." error={fieldError(action.error, "reason")} onChange={(e) => setReason(e.target.value)} />
            <Input
              label="Échéance"
              type="date"
              required
              value={until}
              help="Obligatoire ; au plus la durée de la règle « litige » de l’espace. Le gel peut être levé à tout moment."
              error={fieldError(action.error, "until_on")}
              onChange={(e) => setUntil(e.target.value)}
            />
            <ErrorBox error={action.error && !fieldError(action.error, "reason") && !fieldError(action.error, "until_on") ? action.error : null} />
            <p>
              <Button variant="secondaire" busy={action.busy} onClick={createHold}>
                Geler la candidature
              </Button>
            </p>
          </div>
        ) : null}
      </Card>
      {owner ? (
        <Card title="Anonymiser sur demande">
          <p className="small">
            À la demande de la personne (effacement, opposition) ou lorsque la sélection est terminée, vous pouvez anonymiser cette candidature sans attendre la durée de
            conservation. Si la demande est arrivée par Bailivo, traitez-la plutôt depuis « Données personnelles &gt; Demandes des personnes » pour la clore en même temps.
          </p>
          <p>
            <Button variant="danger" onClick={() => setAnonymizing(true)}>
              Anonymiser cette candidature
            </Button>
          </p>
        </Card>
      ) : null}
      <AnonymizeModal open={anonymizing} applicationIds={[applicationId]} onClose={() => setAnonymizing(false)} onDone={() => navigate(org.to("donnees-personnelles"))} />
    </div>
  );
}
